import React, { useEffect, useState, useRef, useCallback } from "react";
import { useEditor, EditorContent, Editor } from "@tiptap/react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import {
  Bold,
  Italic,
  Link,
  List,
  ListOrdered,
  ChevronDown
} from "lucide-react";
import { Check } from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import IconContainer from "@/components/ui/icon-container";
import { SavedAttachmentInfo } from "@/entities/attachments/types";
import { useTextFormatting } from "./hooks/useTextFormatting";
import { useAttachments } from "./hooks/useAttachments";
import {
  createEditorExtensions,
  createEditorProps,
  createParseOptions,
  processEditorContent,
  processStoredContent
} from "./utils/editorConfig";
import {
  flattenNestedLists,
  restrictedMarkdownToHtml,
  TextEditorFormat
} from "./utils/restrictedMarkdown";
import { textFormatOptions, getTextFormatLabel } from "./utils/textFormatting";
import ToolbarButton from "./components/ToolbarButton";
import ToolbarDivider from "./components/ToolbarDivider";
import AttachmentDisplay from "./components/AttachmentDisplay";
import LinkPopover from "./components/LinkPopover";
import { cn } from "@/utils/utils";
import { useNewFieldsUI } from "@/services/features/useNewFieldsUI";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@/components/ui/tooltip";
const TRANSLATION_KEY = "textEditor.formats";
const BLUR_DEBOUNCE_MS = 500;

// The editor's value as markdown; a restricted editor with no text, such as an empty list left on screen, holds nothing.
const valueOf = (instance: Editor, restricted: boolean) =>
  restricted && !instance.state.doc.textContent.trim()
    ? ""
    : processEditorContent(instance.getHTML(), { keepEscapes: restricted });

interface Props {
  value?: string;
  onBlur?: (value: string) => void;
  onChange?: (value: string) => void;
  placeholder?: string;
  onAttachmentsChange?: (attachments: SavedAttachmentInfo[]) => void;
  initialContent?: string;
  initialAttachments?: SavedAttachmentInfo[] | null;
  autoFocus?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  showFocusState?: boolean;
  minRows?: number;
  className?: string;
  focusedClassName?: string;
  variant?: "form" | "inline";
  // The most characters the user may type.
  maxLength?: number;
  // Only these formats, read and written as CommonMark limited to them.
  formats?: TextEditorFormat[];
  ariaLabelledBy?: string;
}

const TextEditor: React.FC<Props> = ({
  value,
  onBlur,
  onChange,
  maxLength,
  formats,
  ariaLabelledBy,
  placeholder,
  onAttachmentsChange,
  initialContent = "",
  initialAttachments,
  autoFocus = false,
  disabled = false,
  readOnly = false,
  showFocusState = false,
  minRows = 1,
  className,
  focusedClassName,
  variant = "inline"
}) => {
  const { t } = useTranslation();
  const isNewFieldsUI = useNewFieldsUI();
  const [isFocused, setIsFocused] = useState(false);
  const [contentHeight, setContentHeight] = useState(24); // Slightly taller for better usability
  const [showLinkPopover, setShowLinkPopover] = useState(false);
  const editorRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const lastBlurTimestampRef = useRef<number>(0);
  // A restricted value is CommonMark, so the user's literal characters stay escaped.
  const keepEscapes = formats !== undefined;

  const triggerDebouncedBlur = useCallback(
    (editorInstance: Editor | null) => {
      const now = Date.now();
      if (now - lastBlurTimestampRef.current < BLUR_DEBOUNCE_MS) {
        return;
      }
      lastBlurTimestampRef.current = now;

      if (editorInstance && onBlur) {
        try {
          const markdown = valueOf(editorInstance, keepEscapes);
          onBlur(markdown || "");
        } catch (error) {
          console.error("TextEditor blur error:", error);
          onBlur("");
        }
      }
    },
    [onBlur, keepEscapes]
  );

  // Use value prop if provided, otherwise fall back to initialContent
  // Process stored content to ensure it's compatible with the editor
  const rawContent = value !== undefined ? value : initialContent;
  const editorContent = formats
    ? restrictedMarkdownToHtml(rawContent, formats)
    : processStoredContent(rawContent);

  const {
    attachments,
    fileInputRef,
    // handleFileUploadClick,
    handleFileSelect,
    removeAttachment
  } = useAttachments({
    initialAttachments,
    onAttachmentsChange
  });

  const editor = useEditor(
    {
      // The placeholder is always registered so the editor never has to be
      // rebuilt to gain or lose it. Only the redesign paints it, and that is
      // done with a class on the wrapper (see tiptap-inline-placeholder).
      extensions: createEditorExtensions(placeholder, { formats, maxLength }),
      content: editorContent,
      editable: !disabled,
      editorProps: {
        ...createEditorProps(),
        // A restricted editor keeps lists one level deep; pasted ones are lifted.
        ...(formats && { transformPastedHTML: flattenNestedLists }),
        attributes: {
          ...createEditorProps().attributes,
          placeholder: placeholder || "Start typing...",
          ...(ariaLabelledBy && {
            role: "textbox",
            "aria-multiline": "true",
            "aria-labelledby": ariaLabelledBy
          })
        }
      },
      parseOptions: createParseOptions(),
      onFocus: () => {
        if (!disabled) {
          setIsFocused(true);
        }
      },
      onBlur: ({ editor, event }) => {
        if (disabled) return;

        // Check if the blur is happening because user clicked on toolbar
        const relatedTarget = event?.relatedTarget as HTMLElement;
        const isToolbarClick =
          toolbarRef.current?.contains(relatedTarget) ||
          containerRef.current?.contains(relatedTarget);

        if (!isToolbarClick) {
          setIsFocused(false);
          triggerDebouncedBlur(editor);
        }
      },
      onUpdate: ({ editor }) => {
        if (disabled) return;

        if (onChange) {
          onChange(valueOf(editor, keepEscapes));
        }
      }
    }
    // Deliberately no dependency array: useEditor rebuilds the instance when
    // one changes, and rebuilding restores content from the rawContent prop.
    // Keying it on a flag that resolves a tick after mount discarded anything
    // typed in that window, along with cursor position and undo history.
  );

  // `editable` is only read while the editor is being built, so a `disabled`
  // that flips after mount — NextSteps drives it off state — would otherwise
  // leave a locked field still typable.
  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  // The Placeholder extension reads its text from the options captured when the
  // editor was built, and useEditor above deliberately never rebuilds. Callers
  // derive the placeholder from t("add") and the field label, so a locale or
  // label change would otherwise leave the old string painted.
  useEffect(() => {
    if (!editor) return;
    const placeholderExtension = editor.extensionManager.extensions.find(
      (extension) => extension.name === "placeholder"
    );
    if (!placeholderExtension) return;
    placeholderExtension.options.placeholder = placeholder || "";
    // The decoration is recomputed from the options on the next transaction;
    // nothing else would schedule one for a prop-only change.
    editor.view.dispatch(editor.state.tr);
  }, [editor, placeholder]);

  // Handle clicks outside the editor to close toolbar
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsFocused(false);
        setShowLinkPopover(false);
        triggerDebouncedBlur(editor);
      }
    };

    if (isFocused) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isFocused, editor, triggerDebouncedBlur]);

  const {
    showTextFormatDropdown,
    setShowTextFormatDropdown,
    currentTextFormat,
    handleTextFormatChange,
    toggleBold,
    toggleItalic,
    toggleBulletList,
    toggleOrderedList
  } = useTextFormatting({ editor });

  // Auto-expand based on content
  useEffect(() => {
    if (editor && editorRef.current) {
      const updateHeight = () => {
        const editorElement = editorRef.current?.querySelector(".ProseMirror");
        if (editorElement) {
          // Empty editor is always a single row; it grows with content.
          // Gated: base had no such early return, so ungated this shrank a
          // focused empty inline editor from 32 to 24 and dropped the rows a
          // minRows>1 editor reserves — on the flag-off path, for every
          // TextEditor in the app.
          if (isNewFieldsUI && editor.isEmpty) {
            setContentHeight(24);
            return;
          }
          const scrollHeight = editorElement.scrollHeight;
          // For form variant, just use scrollHeight directly without padding
          if (variant === "form") {
            setContentHeight(scrollHeight);
          } else {
            const baseMinHeight = isFocused ? 32 : 24;
            const rowHeight = 24; // Approximate height per row
            const minHeightFromRows = minRows * rowHeight;
            const minHeight = Math.max(baseMinHeight, minHeightFromRows);
            const newHeight = Math.max(
              minHeight,
              Math.min(scrollHeight + 8, 400)
            ); // Min based on rows or base, max 400px
            setContentHeight(newHeight);
          }
        }
      };

      // Update height on content changes
      editor.on("transaction", updateHeight);
      updateHeight(); // Initial height calculation

      return () => {
        editor.off("transaction", updateHeight);
      };
    }
  }, [editor, isFocused, minRows, isNewFieldsUI]);

  useEffect(() => {
    if (editor && rawContent !== undefined && formats) {
      // Compared as the value: the parser's HTML never equals the editor's, so the caret would reset,
      // and an empty list the rep just started would be cleared.
      if (value !== undefined && valueOf(editor, true) !== rawContent) {
        editor.commands.setContent(
          restrictedMarkdownToHtml(rawContent, formats)
        );
      }
      return;
    }
    if (editor && rawContent !== undefined) {
      const processedContent = processStoredContent(rawContent);
      if (editor.getHTML() !== processedContent) {
        if (value !== undefined) {
          editor.commands.setContent(processedContent);
        }
      }
    }
  }, [editor, rawContent, value, formats]);

  useEffect(() => {
    if (editor && autoFocus && !disabled) {
      // Small delay to ensure the editor is fully rendered
      setTimeout(() => {
        editor.commands.focus();
      }, 100);
    }
  }, [editor, autoFocus, disabled]);

  if (!editor) {
    return null;
  }

  if (readOnly) {
    // Read from the editor, not by parsing the HTML: textContent concatenates
    // block elements with no separator, so two paragraphs came out as
    // "RenewalPricing". The editor holds the parsed document, so markdown
    // syntax still does not leak. Strip the zero-width markers the same way
    // hasContent does below — trim() leaves them, and a value holding only
    // blank-line markers otherwise reads as content and hides the placeholder.
    const plainText = editor
      .getText({ blockSeparator: "\n" })
      .split("\u200B")
      .join("")
      .trim();
    const hasAttachments = attachments.length > 0;
    return (
      <div className="px-2 py-2 min-h-9 flex flex-col gap-1">
        {(plainText || !hasAttachments) && (
          <Text
            variant={plainText ? "body" : "bodyTertiary"}
            className="whitespace-pre-wrap"
          >
            {plainText || placeholder}
          </Text>
        )}
        {/* An attachment-only value is still a value: without this the row
            renders blank and reads as empty. */}
        {hasAttachments && (
          <AttachmentDisplay
            attachments={attachments}
            onRemoveAttachment={() => {}}
          />
        )}
      </div>
    );
  }

  const cardHeight = isFocused
    ? Math.max(90, contentHeight + 60)
    : Math.max(24, contentHeight);

  const showToolbar = isFocused && !disabled;
  const allows = (format: TextEditorFormat) =>
    !formats || formats.includes(format);

  // Zero-width blank-line markers stop an "empty" editor reporting isEmpty, so
  // strip them. Attachments live outside the document, so a file-only field
  // would otherwise collapse to the placeholder and hide its own attachment.
  const hasContent =
    attachments.length > 0 ||
    (!editor.isEmpty &&
      editor.getText().split("\u200B").join("").trim().length > 0);

  const legacyContainerClasses =
    variant === "form"
      ? cn(
          "w-full bg-surface-field-default border-[0.5px] border-border-field-default rounded-lg pl-3 pr-2 py-[6px] relative transition-all duration-200 ease-in-out shadow-field min-h-9",
          disabled
            ? "opacity-50 cursor-not-allowed !shadow-none"
            : "hover:border-border-field-hover",
          isFocused &&
            "border-border-field-focus bg-surface-focus-selected shadow-field-focus",
          className
        )
      : cn(
          "w-full relative transition-all duration-200 ease-in-out",
          disabled ? "opacity-60 cursor-not-allowed" : "",
          isFocused &&
            showFocusState &&
            (focusedClassName || "border-primary bg-surface-focus-selected"),
          className
        );

  const newContainerClasses =
    variant === "form"
      ? cn(
          "tiptap-inline-placeholder",
          "w-full bg-surface-field-default border-[0.5px] border-border-field-default rounded-lg pl-3 pr-2 py-[6px] relative transition-colors duration-200 ease-in-out shadow-field min-h-9",
          disabled
            ? "opacity-50 cursor-not-allowed !shadow-none"
            : "hover:border-border-field-hover",
          isFocused &&
            "border-border-field-focus bg-surface-focus-selected shadow-field-focus",
          className
        )
      : cn(
          "tiptap-inline-placeholder",
          "w-full rounded-lg relative transition-all duration-200 ease-in-out",
          isFocused
            ? "border-[0.5px] border-border-field-focus bg-surface-focus-selected"
            : cn(
                "border-[0.5px] border-transparent",
                !disabled && "hover:bg-surface-hover-soft"
              ),
          disabled && "cursor-not-allowed",
          className
        );

  // The file input and the formatting toolbar are identical in both
  // renderings, so they are written once and appended to each wrapper.
  const renderEditorTail = () => (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileSelect}
        className="hidden"
        multiple
        disabled={disabled}
      />

      {showToolbar && (
        <div
          ref={toolbarRef}
          className="absolute bottom-0 left-0 right-0 p-3 flex items-center gap-1 bg-black/80 rounded-b-md transition-all duration-200 ease-in-out"
          // Keeps focus in the editor on a toolbar click; Safari never focuses the button.
          onMouseDown={formats ? (event) => event.preventDefault() : undefined}
        >
          {!formats && (
            <div className="relative">
              <ToolbarButton
                onClick={() =>
                  setShowTextFormatDropdown(!showTextFormatDropdown)
                }
                className="w-fit justify-between px-1 py-1"
                isActive={
                  showTextFormatDropdown || currentTextFormat.variant !== "body"
                }
              >
                <div className="flex items-center gap-1">
                  <Text variant="bodySecondary" className="text-sm">
                    {getTextFormatLabel(currentTextFormat)}
                  </Text>
                  <ChevronDown size={10} />
                </div>
              </ToolbarButton>
              {showTextFormatDropdown && (
                <div
                  className="absolute bottom-full left-0 mb-1 bg-surface-elevation1 border border-border-border1 rounded-md shadow-lg z-50 w-fit"
                  onMouseLeave={() => setShowTextFormatDropdown(false)}
                >
                  {textFormatOptions.map((option) => (
                    <button
                      key={option.variant}
                      onClick={() => handleTextFormatChange(option)}
                      className="w-full px-3 py-2 text-left hover:bg-white/4 transition-colors duration-150 first:rounded-t-md last:rounded-b-md flex items-center justify-between min-w-[200px]"
                    >
                      <div className="flex items-center gap-2">
                        <Text
                          variant="bodySecondary"
                          className="text-sm min-w-[24px]"
                        >
                          {getTextFormatLabel(option)}
                        </Text>
                        <div>
                          <Text variant="body">
                            {t(`${TRANSLATION_KEY}.${option.labelKey}`)}
                          </Text>
                          <Text variant="smBodySecondary" className="block">
                            {t(`${TRANSLATION_KEY}.${option.descriptionKey}`)}
                          </Text>
                        </div>
                      </div>
                      {currentTextFormat.variant === option.variant && (
                        <IconContainer size="5">
                          <Check />
                        </IconContainer>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {!formats && <ToolbarDivider />}
          {allows("bold") && (
            <ToolbarButton
              isActive={editor?.isActive("bold")}
              onClick={toggleBold}
            >
              <Bold size={16} />
            </ToolbarButton>
          )}

          {allows("italic") && (
            <ToolbarButton
              isActive={editor?.isActive("italic")}
              onClick={toggleItalic}
            >
              <Italic size={16} />
            </ToolbarButton>
          )}

          {!formats && <ToolbarDivider />}

          {!formats && (
            <div className="relative">
              <ToolbarButton
                onClick={() => setShowLinkPopover(!showLinkPopover)}
                isActive={showLinkPopover || editor?.isActive("link")}
              >
                <Link size={16} />
              </ToolbarButton>
              <LinkPopover
                editor={editor}
                labels={{
                  enterLink: t("textEditor.linkPopover.enterLink"),
                  linkPlaceholder: t("textEditor.linkPopover.linkPlaceholder"),
                  displayText: t("textEditor.linkPopover.displayText"),
                  displayTextPlaceholder: t(
                    "textEditor.linkPopover.displayTextPlaceholder"
                  ),
                  cancel: t("cancel"),
                  save: t("textEditor.linkPopover.save")
                }}
                isOpen={showLinkPopover}
                onClose={() => setShowLinkPopover(false)}
              />
            </div>
          )}
          {/* <ToolbarButton
            onClick={handleFileUploadClick}
            isActive={attachments.length > 0}
          >
            <Paperclip size={16} />
          </ToolbarButton> */}

          {(allows("bold") || allows("italic")) &&
            (allows("bulletList") || allows("orderedList")) && (
              <ToolbarDivider />
            )}
          {allows("bulletList") && (
            <ToolbarButton
              isActive={editor?.isActive("bulletList")}
              onClick={toggleBulletList}
            >
              <List size={16} />
            </ToolbarButton>
          )}

          {allows("orderedList") && (
            <ToolbarButton
              isActive={editor?.isActive("orderedList")}
              onClick={toggleOrderedList}
            >
              <ListOrdered size={16} />
            </ToolbarButton>
          )}
        </div>
      )}
    </>
  );

  const renderLegacy = () => {
    const Wrapper = variant === "form" ? "div" : Card;
    const wrapperProps =
      variant === "form"
        ? { ref: containerRef, className: legacyContainerClasses }
        : {
            ref: containerRef,
            className: legacyContainerClasses,
            style: { minHeight: `${cardHeight}px` }
          };

    return (
      <Wrapper {...wrapperProps}>
        <div
          ref={editorRef}
          className={cn(
            "transition-all duration-200 ease-in-out",
            variant !== "form" && "pl-1",
            showToolbar ? "pb-14" : "pb-1"
          )}
          style={{
            minHeight: `${variant === "form" ? contentHeight : Math.max(22, contentHeight)}px`
          }}
        >
          <EditorContent
            className={cn(
              "text-primary text-base",
              variant === "form" && "[&_.ProseMirror]:!p-0",
              // Preview rows align on 8px. The editor's own padding is written
              // for the boxed form field, so override it here or the text
              // shifts right the moment the row is focused.
              isNewFieldsUI && variant === "inline" && "[&_.ProseMirror]:!px-2",
              disabled && "pointer-events-none"
            )}
            editor={editor}
          />
          <div
            className={cn(
              // Redesign padding: base rendered pl-3 here for every non-form
              // variant, and DetailFields passes "inline" regardless of the flag.
              isNewFieldsUI && variant === "inline" ? "pl-2" : "pl-3"
            )}
          >
            <AttachmentDisplay
              attachments={attachments}
              onRemoveAttachment={disabled ? () => {} : removeAttachment}
            />
          </div>
        </div>
        {renderEditorTail()}
      </Wrapper>
    );
  };

  const renderNew = () => {
    const wrapperProps =
      variant === "form"
        ? { ref: containerRef, className: newContainerClasses }
        : {
            ref: containerRef,
            className: newContainerClasses,
            style: { minHeight: isFocused ? `${cardHeight}px` : "36px" }
          };

    return (
      <div {...wrapperProps}>
        {variant !== "form" && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className={cn(
                    // No z-index: absolute positioning alone already paints this
                    // above the in-flow editorRef sibling; a z-index here instead
                    // competed with the sticky page header at the root context.
                    "absolute inset-0 h-9 flex items-center px-2 overflow-hidden cursor-text",
                    "transition-opacity duration-200 ease-in-out",
                    isFocused ? "opacity-0 pointer-events-none" : "opacity-100"
                  )}
                  onClick={() => {
                    if (!disabled) {
                      setIsFocused(true);
                      setTimeout(() => editor.commands.focus("end"), 0);
                    }
                  }}
                >
                  <Text
                    variant="body"
                    className={cn(
                      "truncate min-w-0",
                      !hasContent ? "!text-tertiary" : "",
                      hasContent && disabled ? "!text-body-disabled" : ""
                    )}
                  >
                    {/* hasContent counts attachments, but getText() is empty for
                        an attachment-only value — naming the files keeps the
                        collapsed row from reading as empty. */}
                    {hasContent
                      ? editor.getText() ||
                        attachments
                          .map((attachment) => attachment.filename)
                          .join(", ")
                      : placeholder || ""}
                  </Text>
                </div>
              </TooltipTrigger>
              {hasContent && (
                <TooltipContent
                  side="bottom"
                  maxWidth={24}
                  className="break-words whitespace-pre-wrap"
                >
                  {editor.getText() ||
                    attachments
                      .map((attachment) => attachment.filename)
                      .join(", ")}
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        )}

        <div
          ref={editorRef}
          className={cn(
            "transition-all duration-200 ease-in-out",
            showToolbar ? "pb-14" : "pb-1",
            variant !== "form" &&
              (!isFocused
                ? "opacity-0 pointer-events-none overflow-hidden"
                : "opacity-100")
          )}
          style={
            variant !== "form" && !isFocused
              ? { height: 0 }
              : {
                  minHeight: `${variant === "form" ? contentHeight : Math.max(22, contentHeight)}px`
                }
          }
        >
          <EditorContent
            className={cn(
              "text-base",
              disabled
                ? "text-body-disabled pointer-events-none"
                : "text-primary",
              variant === "form" && "[&_.ProseMirror]:!p-0",
              isNewFieldsUI && variant === "inline" && "[&_.ProseMirror]:!px-2"
            )}
            editor={editor}
          />
          <div
            className={cn(
              // Redesign padding: base rendered pl-3 here for every non-form
              // variant, and DetailFields passes "inline" regardless of the flag.
              isNewFieldsUI && variant === "inline" ? "pl-2" : "pl-3"
            )}
          >
            <AttachmentDisplay
              attachments={attachments}
              onRemoveAttachment={disabled ? () => {} : removeAttachment}
            />
          </div>
        </div>
        {renderEditorTail()}
      </div>
    );
  };

  return isNewFieldsUI ? renderNew() : renderLegacy();
};

export default TextEditor;
